/* eslint-disable jsx-a11y/anchor-has-content */
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { toast } from 'react-toastify'
import classNames from 'classnames'
import { useTranslation, Trans } from 'react-i18next'

import classes from './PersonalForm.module.scss'
import Input from '../../common/form/Input'
import Select from '../../common/form/Select'
import moment from 'moment'
import { flatServerFormErrors } from '../../../utility/helperFunctions'
import { GENDER_OPTIONS, FAMILY_ADMIN_MIN_AGE } from '../../../misc/Constants'
import { useTranslated } from '../../../i18n'
import { translateOptions } from '../../../utility/i18nConverters'
import { AccountContext } from '../AccountContext'
import { useContext } from 'react'

const CHANGE_EMAIL_URL = `${process.env.REACT_APP_OAUTH_PROVIDER_HOST}/profile/edit`

// eslint-disable-next-line max-lines-per-function, complexity
const PersonalForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { user, submitDisabled, updateAction } = useContext(AccountContext) // Note: user could be golfer OR current_user
  const currentUser = useSelector(state => state.user.current_user)
  const genderOptions = useTranslated(GENDER_OPTIONS, translateOptions)

  const [isUpdating, setIsUpdating] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  const [phoneData, setPhoneData] = useState({
    phone: user?.phone || '',
    phonePrefix: user?.phone_prefix || '',
    phonePrefixCountry: user?.phone_prefix_country || '',
  })

  // Note: DatePicker does not work as uncontrolled input; state only used to update the UI
  const [controlledBirthdate, setControlledBirthdate] = useState(user?.birthdate ? moment(user?.birthdate) : null)

  const isCurrentUser = user.id === currentUser.id
  const isCoach = currentUser.type === 'Coach'
  const isFamilyAdmin = currentUser.type === 'Student' && currentUser.family_admin
  const userIsCoach = user?.type === 'Coach'
  const userIsFamilyAdmin = user?.family_admin
  const familyAdminEditingJunior = isFamilyAdmin && !isCurrentUser && !userIsFamilyAdmin

  const accountTypeKey = userIsCoach ? 'COACH' : userIsFamilyAdmin ? 'ADULT_GOLFER' : 'JUNIOR_GOLFER'

  // Readonly when: coach editing others, non-family-admin editing others, or family admin editing another family admin
  const isReadonly = !isCurrentUser && (isCoach || !isFamilyAdmin || userIsFamilyAdmin)

  const validateForm = formData => {
    setErrors({})
    const formErrors = {}

    const firstName = formData.get('firstName')
    const lastName = formData.get('lastName')
    const birthdate = moment(formData.get('birthdate'), 'MM/DD/YYYY')

    if (!firstName) {
      formErrors['first_name'] = t('account.personal.firstNameRequired')
    }

    if (!lastName) {
      formErrors['last_name'] = t('account.personal.lastNameRequired')
    }

    if (familyAdminEditingJunior && !formData.get('username')) {
      formErrors['username'] = t('account.personal.usernameRequired')
    }

    if (!birthdate.isValid()) {
      formErrors['birthdate'] = t('account.personal.dateInvalid')
    } else if (userIsFamilyAdmin && moment().diff(birthdate, 'years') < FAMILY_ADMIN_MIN_AGE) {
      formErrors['birthdate'] = t('account.personal.minAgeError', { age: FAMILY_ADMIN_MIN_AGE })
    }

    if (Object.keys(formErrors)?.length) {
      setErrors(formErrors)
      return false
    }

    return true
  }

  const handleSubmitForm = async e => {
    e.preventDefault()
    const formData = new FormData(e.target)

    if (validateForm(formData)) {
      const partialUser = {
        id: user.id,
        first_name: formData.get('firstName'),
        last_name: formData.get('lastName'),
        ...(familyAdminEditingJunior && { username: formData.get('username') }),
        phone: phoneData.phone,
        phone_prefix: phoneData.phonePrefix,
        phone_prefix_country: phoneData.phonePrefixCountry,
        birthdate: moment(formData.get('birthdate'), 'MM/DD/YYYY').format('YYYY-MM-DD'),
        gender: formData.get('gender'),
      }

      try {
        setIsUpdating(true)
        const newState = await dispatch(updateAction(partialUser))

        if (newState.type === 'UI_ERROR') {
          if (newState.status === 503) {
            toast.error(t('account.personal.updateUnavailable'))
          } else if (newState.error?.general) {
            toast.error(newState.error.general.join(', '))
          } else {
            setErrors(flatServerFormErrors(newState?.error))
          }
        } else {
          toast.success(t('account.common.userUpdated'))
        }
      } catch {
        toast.error(t('account.personal.updateError'))
      } finally {
        setIsUpdating(false)
        setHasChanges(false)
      }
    }
  }

  const handleDetectChanges = (e) => {
    const formData = new FormData(e.currentTarget)
    const firstName = formData.get('firstName')
    const lastName = formData.get('lastName')
    const username = formData.get('username')
    const birthdate = moment(formData.get('birthdate'), 'MM/DD/YYYY')
    const gender = Number(formData.get('gender'))

    const hasChanges_ =
      user?.first_name !== firstName ||
      user?.last_name !== lastName ||
      (familyAdminEditingJunior && user?.username !== username) ||
      user?.phone !== phoneData.phone ||
      user?.phone_prefix !== phoneData.phonePrefix ||
      user?.phone_prefix_country !== phoneData.phonePrefixCountry ||
      moment(user?.birthdate).format('YYYY-MM-DD') !== birthdate.format('YYYY-MM-DD') ||
      (user?.gender || '') !== (gender || '')

    if (hasChanges_ !== hasChanges) {
      setHasChanges(hasChanges_)
    }
  }

  const handleBirthdateSelect = (e) => {
    setControlledBirthdate(e)
    handleDetectChanges(e)
  }

  const handlePhoneChange = (phoneDataObj) => {
    setPhoneData({
      phone: phoneDataObj.phone || '',
      phonePrefix: phoneDataObj.phonePrefix || '',
      phonePrefixCountry: phoneDataObj.phonePrefixCountry || '',
    })
    setHasChanges(true)
  }

  return (
    <form onSubmit={handleSubmitForm} onInput={handleDetectChanges} className={classNames(classes.container, 'animated fadeIn')}>
      <div className='row'>
        <h4 className={classes.title}>{t('account.personal.personalDetails')}</h4>
      </div>

      <div className='row d-flex'>
        <div className='col-md-12 col-xl-6 p-0 pr-xl-4 mb-3'>
          <label htmlFor='firstName'>{t('common.field.firstName')}</label>
          <Input
            type='text'
            id='firstName'
            name='firstName'
            defaultValue={user?.first_name}
            placeholder={t('common.field.firstName')}
            classNames={{ inputContainer: classes.accountInputContainer }}
            disabled={isReadonly}
            error={errors?.first_name}
          />
        </div>
        <div className='col-md-12 col-xl-6 p-0 pl-xl-4 mb-3'>
          <label htmlFor='lastName'>{t('common.field.lastName')}</label>
          <Input
            type='text'
            id='lastName'
            name='lastName'
            defaultValue={user?.last_name}
            placeholder={t('common.field.lastName')}
            classNames={{ inputContainer: classes.accountInputContainer }}
            disabled={isReadonly}
            error={errors?.last_name}
          />
        </div>
      </div>

      {familyAdminEditingJunior && (
        <div className='row mb-3'>
          <label htmlFor='username'>{t('common.field.username')}</label>
          <Input
            type='text'
            id='username'
            name='username'
            defaultValue={user?.username}
            placeholder={t('common.field.username')}
            classNames={{ inputContainer: classes.accountInputContainer }}
            error={errors?.username}
          />
        </div>
      )}

      <div className='row mb-3'>
        <label htmlFor='phone'>{t('common.field.phoneNumber')}</label>
        <Input
          type='telephone'
          id='phone'
          name='phone'
          defaultValue={user?.phone_prefix && user?.phone ? `${user.phone_prefix}${user.phone}` : user?.phone}
          placeholder={t('common.field.phoneNumber')}
          classNames={{ inputContainer: classes.accountInputContainerPhone }}
          disabled={isReadonly}
          error={errors?.phone}
          onChange={handlePhoneChange}
        />
      </div>

      <div className='row'>
        <div className='col-md-12 col-xl-6 p-0 pr-xl-4 mb-3'>
          <label htmlFor='birthdate'>{t('common.field.birthdate')}</label>
          <Input
            type='date'
            id='birthdate'
            name='birthdate'
            placeholder={t('common.field.birthdate')}
            selected={controlledBirthdate}
            onChange={handleBirthdateSelect}
            classNames={{
              inputContainer: classes.accountInputContainer,
              symbol: classes.inputIcon,
            }}
            symbol={<i className='fa fa-calendar'/>}
            error={errors?.birthdate}
            disabled={isReadonly}
          />
        </div>
        <div className='col-md-12 col-xl-6 p-0 pl-xl-4 mb-3'>
          <label htmlFor='gender'>{t('common.field.gender')}</label>
          <Select
            id='gender'
            name='gender'
            placeholder={t('account.personal.selectGender')}
            defaultValue={user?.gender !== undefined ? genderOptions.find(gender => gender.value === user?.gender) : undefined}
            options={genderOptions}
            onChange={handleDetectChanges}
            classNames={{ singleValue: () => classes.genderInputWrapper }}
            isDisabled={isReadonly}
            error={errors?.gender}
            isSearchable={false}
          />
        </div>
      </div>

      {isCurrentUser && (
        <div className='row mb-4'>
          <div className='col p-0'>
            <h5 className='mb-2'>{userIsFamilyAdmin ? t('common.field.email') : t('common.field.username')}</h5>
            <p className={classNames(classes.accountType, 'mb-1')}>
              {userIsFamilyAdmin ? user?.email : user?.username}
            </p>
            <p className={classes.infoText}>
              {userIsFamilyAdmin ? (
                <Trans
                  i18nKey='account.personal.changeEmailPrompt'
                  components={{ a: <a href={CHANGE_EMAIL_URL} target='_blank' rel='noopener noreferrer' /> }}
                />
              ) : (
                <Trans
                  i18nKey='account.personal.changeUsernamePrompt'
                  components={{ a: <a href={CHANGE_EMAIL_URL} target='_blank' rel='noopener noreferrer' /> }}
                />
              )}
            </p>
          </div>
        </div>
      )}

      <div className='row mb-4'>
        <div className='col p-0'>
          <h5 className='mb-2'>{t('account.personal.accountType')}</h5>
          <p className={classNames(classes.accountType, 'mb-1')}>{t(`account.personal.accountTypes.${accountTypeKey}`)}</p>
          <p className={classes.infoText}>{t(`account.personal.accountTypeDescriptions.${accountTypeKey}`)}</p>
          {isCurrentUser && !userIsCoach && (
            <p className={classNames(classes.infoText, 'mt-2')}>
              {userIsFamilyAdmin ? (
                <Trans
                  i18nKey='account.personal.changeAccountTypeFamilyAdmin'
                  components={{ a: <a href={CHANGE_EMAIL_URL} target='_blank' rel='noopener noreferrer' /> }}
                />
              ) : (
                <Trans
                  i18nKey='account.personal.changeAccountTypeJunior'
                  components={{ a: <a href={CHANGE_EMAIL_URL} target='_blank' rel='noopener noreferrer' /> }}
                />
              )}
            </p>
          )}
        </div>
      </div>

      <div className='row mt-5'>
        <button
          type='submit'
          className='btn btn-primary'
          disabled={isUpdating || isReadonly || submitDisabled || !hasChanges}
        >
          {isUpdating ? t('account.common.savingChanges') : t('common.action.saveChanges')}
        </button>
      </div>
    </form>
  )
}

export default PersonalForm
